import { Dayjs } from 'dayjs';
import { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'services/dayjs';
import {
  TablePages,
  tablePagesFromString,
  tableScreenInputFromString,
  tableScreenInputToString,
  TableScreenQueryParam,
} from 'screens/tables-screen-input';
import useDebouncedFunc from 'hooks/useDebouncedFunc';
import useNumberParams from 'hooks/useNumberParams';
import useProfileConfig from 'hooks/useProfileConfig';
import useSearch from 'hooks/useSearch';
import useUser from 'hooks/useUser';
import { DateType, NodeInput, TablesScreenInput } from 'generated/graphql';

type TablesInputContextType = {
  input: TablesScreenInput;
  profileID: string;
  isQueryPaused: boolean;
  dateRange: {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
    minDate?: Dayjs;
    label?: string;
  };
  search: string;
  activeTabIndex: number;
  activeGroupIndex: number;
  setDateRange: (start: Dayjs | null, end: Dayjs | null) => void;
  resetDateRange: () => void;
  setSearch: (search: string) => void;
  setActiveTab: (tabIndex: number) => void;
  setActiveGroup: (groupIndex: number) => void;
  setPage: (page: number, identifier: string) => void;
  pageOf: (identifier: string) => number;
  // A tab, group or table loads on its own with this, as the screen's search
  // and dates filter it: a lazy tab or group once the user opens it, and a
  // table when the user pages it.
  nodeInput: (identifier: string) => NodeInput;
};

const DATE_FORMAT = 'D MMM';

export const formatDateRangeLabel = (startDate: Dayjs | null, endDate: Dayjs | null): string | undefined => {
  if (!startDate || !endDate) return undefined;
  if (startDate.isSame(endDate, 'day')) return startDate.format(DATE_FORMAT);
  return `${startDate.format(DATE_FORMAT)} - ${endDate.format(DATE_FORMAT)}`;
};

/** Parse the initial input state from the URL `i` param on mount. */
const parseInitialInput = (inputParam: string | undefined) => {
  if (!inputParam) return null;
  return tableScreenInputFromString(inputParam);
};

const TablesInputContext = createContext<TablesInputContextType | null>(null);

export const TablesInputProvider = ({
  dateType,
  defaultDateRange,
  profileID: screenProfileID,
  children,
}: {
  dateType?: DateType | undefined;
  defaultDateRange?: {
    startDate: Dayjs | null;
    endDate: Dayjs | null;
  };
  // The profile the screen views, for a screen that picks it itself rather
  // than from the URL, e.g. the quotes screen's buyer selector.
  profileID?: string;
  children: ReactNode;
}) => {
  const { isPendingReview, isRegistering, user, isAdmin } = useUser();
  const [getParam, setParam] = useSearch();
  const [getNumberParam, setNumberParam] = useNumberParams();
  const { profileID: paramProfileID } = useProfileConfig();
  const searchParam = getParam(TableScreenQueryParam.Search);
  const activeTabIndex = getNumberParam(TableScreenQueryParam.Tab, 0) ?? 0;
  const activeGroupIndex = getNumberParam(TableScreenQueryParam.Group, 0) ?? 0;
  // Restore persisted state from URL on mount
  const initialInput = useRef(parseInitialInput(getParam(TableScreenQueryParam.Input))).current;
  const [startDate, setStartDate] = useState<Dayjs | null>(() => {
    const iso = initialInput?.dates[0]?.start;
    return iso ? dayjs(iso) : (defaultDateRange?.startDate ?? null);
  });
  const [endDate, setEndDate] = useState<Dayjs | null>(() => {
    const iso = initialInput?.dates[0]?.end;
    return iso ? dayjs(iso) : (defaultDateRange?.endDate ?? null);
  });
  const [pages, setPages] = useState<TablePages>(() =>
    tablePagesFromString(getParam(TableScreenQueryParam.Input) ?? ''),
  );
  // Determine the active profile ID (admin override or user's own profile)
  const profileID = useMemo(
    () => screenProfileID ?? (isAdmin && paramProfileID ? paramProfileID : (user?.profileId?.toString() ?? '0')),
    [screenProfileID, isAdmin, paramProfileID, user?.profileId],
  );

  const input = useMemo<TablesScreenInput>(
    () => ({
      search: searchParam || '',
      dates:
        !startDate || !endDate
          ? []
          : [
              {
                start: startDate.toISOString(),
                end: endDate.toISOString(),
                type: dateType || DateType.ExpectedReceiveDate,
              },
            ],
    }),
    [searchParam, startDate, endDate, dateType],
  );

  const pageOf = useCallback((identifier: string) => pages[identifier] ?? 0, [pages]);

  const nodeInput = useCallback(
    (identifier: string): NodeInput => ({ identifier, filter: { search: input.search, dates: input.dates } }),
    [input],
  );

  // Sync input to URL so it survives page refreshes and can be shared
  useEffect(() => {
    const encoded = tableScreenInputToString(input, pages);
    setParam(TableScreenQueryParam.Input, encoded, { replace: true });
  }, [input, pages, setParam]);

  const isQueryPaused = useMemo(
    () => !input || isPendingReview || isRegistering || !user || !user?.profileId || !user?.status || !profileID,
    [input, isPendingReview, isRegistering, user, profileID],
  );

  const dateRange = useMemo(
    () => ({
      label: formatDateRangeLabel(startDate, endDate),
      startDate,
      endDate,
    }),
    [startDate, endDate],
  );

  // A page is a page of the rows the input filters, so a new search or new
  // dates start every table back at its first.
  const setDateRange = useCallback((start: Dayjs | null, end: Dayjs | null) => {
    setStartDate(start);
    setEndDate(end);
    setPages({});
  }, []);

  const resetDateRange = useCallback(() => {
    setStartDate(defaultDateRange?.startDate ?? null);
    setEndDate(defaultDateRange?.endDate ?? null);
    setPages({});
  }, [defaultDateRange]);

  const debouncedSetSearchParam = useDebouncedFunc((search: string) => {
    setParam(TableScreenQueryParam.Search, search, { replace: true });
    setPages({});
  }, 300);

  const setSearch = useCallback(
    (search: string) => {
      debouncedSetSearchParam(search);
    },
    [debouncedSetSearchParam],
  );

  const setActiveTab = useCallback(
    (tabIndex: number) => {
      setNumberParam(TableScreenQueryParam.Tab, tabIndex);
      setNumberParam(TableScreenQueryParam.Group, 0);
    },
    [setNumberParam],
  );

  const setActiveGroup = useCallback(
    (groupIndex: number) => {
      setNumberParam(TableScreenQueryParam.Group, groupIndex);
    },
    [setNumberParam],
  );

  const setPage = useCallback((page: number, identifier: string) => {
    setPages((prev) => ({ ...prev, [identifier]: page }));
  }, []);

  const value = useMemo<TablesInputContextType>(
    () => ({
      input,
      profileID,
      isQueryPaused,
      dateRange,
      search: searchParam || '',
      activeTabIndex,
      activeGroupIndex,
      setDateRange,
      resetDateRange,
      setSearch,
      setActiveTab,
      setActiveGroup,
      setPage,
      pageOf,
      nodeInput,
    }),
    [
      input,
      profileID,
      isQueryPaused,
      dateRange,
      searchParam,
      activeTabIndex,
      activeGroupIndex,
      setDateRange,
      resetDateRange,
      setSearch,
      setActiveTab,
      setActiveGroup,
      setPage,
      pageOf,
      nodeInput,
    ],
  );

  return <TablesInputContext.Provider value={value}>{children}</TablesInputContext.Provider>;
};

export const useTablesInput = (): TablesInputContextType => {
  const context = useContext(TablesInputContext);

  if (!context) {
    throw new Error('useTablesInput must be used within a TablesInputProvider');
  }

  return context;
};
